import React from "react";
import DropDown from "../../../../../common/components/shared/dropDown/dropDown";
import Row from "../../../../../common/components/shared/row/row";
import {storesContext} from "../../../../../common/stores/stores";
import i18n from "../../../../../i18n/i18n";
import {CargoType} from "../../../../models/cargo";
import {ITicketStore} from "../../../../stores/ticketStore";
import Validator from "../../../../../helpers/validator";
import TextInput from "../../../../../common/components/shared/textInput/textInput";
import {isDangerousGood} from "../../../../../helpers/dangerousGoods";
import CargoTypeDropdownItems from "../../../../../helpers/cargoTypeDropdownItems";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../../common/models/httpError";
import {ValidatedValue} from "../../../../../helpers/validatedValue";
import {SupervisorFlowAction} from "../../../../models/order";
import {errorMessages} from "../../../../../helpers/errorMessage";
import ChangeStatusPopup from "../../../orders/orderForm/generalInformationSection/changeStatusPopup/changeStatusPopup";
import {Api} from "../../../../../helpers/api";
import Button from "../../../../../common/components/shared/button/button";
import {TicketService} from "../../../../services/ticketService";
import {TransportationService} from "../../../../services/transportationService";
import {observer} from "mobx-react";

interface GeneralInformationSectionProps {
    isUpdate: boolean;
    ticketId?: string;
}

interface GeneralInformationSectionState {
    availableAction: AxiosResponse<HttpErrorModel[] | any> | undefined;
    changeStatusResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayPopup: "changeStatus" | null;
    selectedAction: ValidatedValue<SupervisorFlowAction | null>;
    isChangeStatusWaiting: boolean;
}

const notEmptyValidator = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

class GeneralInformationSection extends React.Component <GeneralInformationSectionProps, GeneralInformationSectionState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            availableAction: undefined,
            changeStatusResponse: undefined,
            displayPopup: null,
            selectedAction: new ValidatedValue(null, [notEmptyValidator]),
            isChangeStatusWaiting: false
        };
    }

    render() {
        const ticketStore = this.context.ticketStore as ITicketStore;
        const currentStatus = new ValidatedValue(i18n.t(`orderStatus.${ticketStore.status.value}`));

        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.ticket.details.general.header")}
                </div>
                {this.state.displayPopup === "changeStatus" && this.props.ticketId && this.renderChangeStatusPopup()}
                <div className="section-content">
                    <Row class="flex-type gap-30">
                        <div className="status-section">
                            <TextInput
                                label={i18n.t("transportation.ticket.details.general.properties.status")}
                                validatedValue={currentStatus}
                                testId="status"
                                disabled
                                className={"status-input"}
                            />
                            {this.props.isUpdate && this.renderChangeStatusBtn()}
                        </div>
                        <DropDown
                            className="element"
                            label={i18n.t("transportation.ticket.details.general.properties.cargoType")}
                            items={CargoTypeDropdownItems.getCargoTypeDropdownItems()}
                            validatedValue={ticketStore.cargoType}
                            testId="cargo-type"
                            onChange={(newValue) => {
                                this.addValidatorsToDimensions(newValue, ticketStore);
                                this.addValidatorsToDangerousGoods(newValue, ticketStore);
                            }}
                        />
                        <TextInput
                            label={i18n.t("transportation.ticket.details.general.properties.barcodeNo")}
                            validatedValue={ticketStore.barcodeNo}
                            testId="barcode-no"
                            offAutocomplete
                        />
                        <TextInput
                            label={i18n.t("transportation.ticket.details.general.properties.currentRoute")}
                            validatedValue={ticketStore.currentRoute}
                            testId="current-route"
                            disabled={this.props.isUpdate}
                        />
                        <TextInput
                            label={i18n.t("transportation.ticket.details.general.properties.gateNo")}
                            validatedValue={ticketStore.gateNo}
                            testId="gate-no"
                            disabled={this.props.isUpdate}
                        />
                    </Row>
                </div>
            </div>
        );
    }

    private addValidatorsToDimensions(cargoType: CargoType, ticketStore: ITicketStore) {
        ticketStore.length.removeValidators();
        ticketStore.width.removeValidators();
        ticketStore.height.removeValidators();

        ticketStore.length.clearValidation();
        ticketStore.width.clearValidation();
        ticketStore.height.clearValidation();

        const onlyNumberValidator = {
            name: "onlyNumber",
            fn: Validator.isOnlyPositiveNumberWithCommaOrEmpty,
            errorMessage: () => i18n.t("errorMessages.onlyNumber")
        };

        const requireWhenCargoTypeIsOversizeValidator = {
            name: "requireWhenCargoTypeIsOversizeValidator",
            fn: (value: number) => {
                return (cargoType === CargoType.OVERSIZE && !!value) ||
                    (cargoType === CargoType.OVERSIZE_LORRY && !!value) ||
                    (cargoType === CargoType.OVERSIZE_TRAILER && !!value) ||
                    cargoType !== CargoType.OVERSIZE;
            },
            errorMessage: () => `${i18n.t("errorMessages.required")}`
        };

        ticketStore.length.addValidators([requireWhenCargoTypeIsOversizeValidator, onlyNumberValidator]);
        ticketStore.width.addValidators([requireWhenCargoTypeIsOversizeValidator, onlyNumberValidator]);
        ticketStore.height.addValidators([requireWhenCargoTypeIsOversizeValidator, onlyNumberValidator]);
    }

    private addValidatorsToDangerousGoods(cargoType: CargoType, ticketStore: ITicketStore) {
        const requireUnCodeValidator = {
            name: "requireUnCodeValidator",
            fn: (value: string) => {
                return (isDangerousGood(cargoType) && !!value) || !isDangerousGood(cargoType);
            },
            errorMessage: () => `${i18n.t("errorMessages.required")}`
        };

        ticketStore.dangerousGoods.forEach((item) => {
            item.unCodeId.removeValidators();
            item.packingId.removeValidators();
            item.packingQuantity.removeValidators();
            item.packingGroup.removeValidators();
            item.weight.removeValidators();

            item.unCodeId.clearValidation();
            item.packingId.clearValidation();
            item.packingQuantity.clearValidation();
            item.packingGroup.clearValidation();
            item.weight.clearValidation();

            item.unCodeId.addValidators([requireUnCodeValidator]);
            item.weight.addValidators([requireUnCodeValidator]);
        });
    }

    private renderChangeStatusPopup() {
        return (
            <ChangeStatusPopup
                displayPopup
                closePopup={this.closeStatusPopup.bind(this)}
                confirm={this.changeStatus.bind(this)}
                response={this.state.changeStatusResponse}
                orderId={this.props.ticketId!}
                isWaiting={this.state.isChangeStatusWaiting}
                selectedAction={this.state.selectedAction}
                onChangeDropdown={(newValue: SupervisorFlowAction) => {
                    this.state.selectedAction.setValue(newValue);
                }}
            />
        );
    }

    private closeStatusPopup() {
        this.setState({
            displayPopup: null,
            availableAction: undefined,
            changeStatusResponse: undefined,
        });
        this.state.selectedAction.setValue(null);
    }

    private async changeStatus() {
        this.state.selectedAction.validate();
        const ticketStore = this.context.ticketStore as ITicketStore;
        if (this.state.selectedAction.value && this.props.ticketId) {
            this.setState({
                isChangeStatusWaiting: true
            });
            const response = await TransportationService.executeSupervisorAction(this.props.ticketId, this.state.selectedAction.value);
            this.setState({
                changeStatusResponse: response,
            });
            if (Api.isSuccess(response)) {
                const ticket = await TicketService.getTicket(this.props.ticketId);
                ticketStore.status.setValue(ticket.data.status);
                this.closeStatusPopup();
            }
            this.setState({
                isChangeStatusWaiting: false
            });
        }
    }

    private renderChangeStatusBtn() {
        return (
            <Button
                testId="change-status-button"
                variant={"text"}
                className="change-status-button"
                type="secondary"
                text={i18n.t("transportation.ticket.actions.modifyStatus")}
                onClick={() => {
                    this.setState({displayPopup: "changeStatus"});
                }}
            />
        );
    }
}

export default observer(GeneralInformationSection);